/**
 * Slideshow Module
 * Uses two synchronized Splide instances:
 * - Content slider with fade effect
 * - Image slider with slide effect
 */

import Splide from "@splidejs/splide";
import { debounce, log } from "./utils.js";

const TRANSITION_SLIDESHOW_SINGLE = {
    speed: 900,
    easing: "cubic-bezier(0.22, 1, 0.36, 1)",
};

const TRANSITION_SLIDESHOW_MULTIPLE = {
    speed: 650,
    easing: "cubic-bezier(0.16, 1, 0.3, 1)",
};
const TEXT_ACCENT_READY_CLASS = "is-text-accent-ready";

function getSplideSpeed(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function measureTextAccentBlock(accentBlock) {
    const title = accentBlock.querySelector(".c-text-block__title");
    if (!title) {
        accentBlock.classList.remove(TEXT_ACCENT_READY_CLASS);
        return;
    }

    const lineSource = title.querySelector("span") || title;
    const rects = [];
    const textNodeWalker = document.createTreeWalker(
        lineSource,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode(node) {
                return node.textContent && node.textContent.trim().length > 0
                    ? NodeFilter.FILTER_ACCEPT
                    : NodeFilter.FILTER_REJECT;
            },
        },
    );

    // Per-character ranges yield stable line fragments even with inline + box-decoration-break.
    let textNode = textNodeWalker.nextNode();
    while (textNode) {
        const textContent = textNode.textContent || "";
        for (
            let charIndex = 0;
            charIndex < textContent.length;
            charIndex += 1
        ) {
            if (textContent[charIndex].trim() === "") {
                continue;
            }
            const charRange = document.createRange();
            charRange.setStart(textNode, charIndex);
            charRange.setEnd(textNode, charIndex + 1);
            const charRects = Array.from(charRange.getClientRects()).filter(
                (rect) => rect.width > 0.5 && rect.height > 0.5,
            );
            rects.push(...charRects);
        }
        textNode = textNodeWalker.nextNode();
    }

    if (rects.length === 0) {
        accentBlock.classList.remove(TEXT_ACCENT_READY_CLASS);
        title.style.removeProperty("--accent-title-before-right");
        return;
    }

    const firstLineTop = Math.min(...rects.map((rect) => rect.top));
    const firstLineRects = rects.filter(
        (rect) => Math.abs(rect.top - firstLineTop) < 2,
    );
    const firstLineRight = Math.max(
        ...firstLineRects.map((rect) => rect.right),
    );

    // Per CSS spec (section 10.1): for an inline position:relative element the
    // containing block's right edge is the right content edge of the *last* line
    // box — not the bounding-box right. So CSS `right` on ::before is measured
    // from the last line's right edge, and we must offset accordingly.
    const lastLineTop = Math.max(...rects.map((rect) => rect.top));
    const lastLineRects = rects.filter(
        (rect) => Math.abs(rect.top - lastLineTop) < 2,
    );
    const lastLineRight = Math.max(...lastLineRects.map((rect) => rect.right));

    // Negative when first line is wider than last — intentional, allows ::before
    // to extend right of the last line's edge to reach the first line's end.
    const rightOffset = lastLineRight - firstLineRight;
    const normalizedOffset = `${Math.round(rightOffset * 100) / 100}px`;

    requestAnimationFrame(() => {
        title.style.setProperty(
            "--accent-title-before-right",
            normalizedOffset,
        );
        accentBlock.classList.add(TEXT_ACCENT_READY_CLASS);
    });
}

function updateTextAccentMeasurements(rootElement) {
    const accentBlocks = rootElement.querySelectorAll(".t-accent--text-block");
    accentBlocks.forEach((accentBlock) => {
        measureTextAccentBlock(accentBlock);
    });
}

/**
 * Initialize all slideshows on the page
 */
export function initSlideshow() {
    const slideshows = document.querySelectorAll("[data-slideshow]");

    if (slideshows.length === 0) {
        return;
    }

    slideshows.forEach((element) => {
        const contentSlider = element.querySelector("[data-slideshow-content]");
        const imageSlider = element.querySelector("[data-slideshow-images]");

        if (!contentSlider || !imageSlider) {
            log("Slideshow: Missing content or image slider");
            return;
        }

        const speed = getSplideSpeed(
            element.dataset.splideSpeed,
            TRANSITION_SLIDESHOW_SINGLE.speed,
        );
        const rewind = element.dataset.splideRewind === "1";
        // WCAG 2.2.2: keep slides static unless explicit pause/play controls exist.
        const autoplay = false;
        const interval = parseInt(element.dataset.splideInterval, 10) || 5000;
        const pauseOnHover = element.dataset.splidePauseOnHover === "1";
        const showArrows = element.dataset.splideArrows !== "0";

        // Content slider - fade effect
        const contentSplide = new Splide(contentSlider, {
            type: "fade",
            rewind: rewind,
            speed: speed,
            autoplay: autoplay,
            interval: interval,
            pauseOnHover: pauseOnHover,
            arrows: false,
            pagination: false,
            drag: false,
        });

        // Image slider - slide effect with easing
        const imageSplide = new Splide(imageSlider, {
            type: "slide",
            rewind: rewind,
            speed: speed,
            easing: TRANSITION_SLIDESHOW_SINGLE.easing,
            autoplay: false, // Controlled by content slider
            arrows: false,
            pagination: false,
        });

        // Reset zoom on all slides before move, apply zoom-out on active after move
        imageSplide.on("move", () => {
            const slides = imageSlider.querySelectorAll(".splide__slide");
            slides.forEach((slide) => {
                slide.classList.remove("is-zooming");
            });
        });

        imageSplide.on("moved", (newIndex) => {
            const activeSlide =
                imageSlider.querySelectorAll(".splide__slide")[newIndex];
            if (activeSlide) {
                // Small delay to ensure the slide transition has started
                requestAnimationFrame(() => {
                    activeSlide.classList.add("is-zooming");
                });
            }
        });

        // Sync the sliders
        contentSplide.sync(imageSplide);

        // Mount both sliders
        contentSplide.mount();
        imageSplide.mount();

        // Set initial zoom state on first slide
        const firstSlide = imageSlider.querySelector(
            ".splide__slide.is-active",
        );
        if (firstSlide) {
            firstSlide.classList.add("is-zooming");
        }

        // Custom navigation buttons
        const prevBtn = element.querySelector("[data-slideshow-prev]");
        const nextBtn = element.querySelector("[data-slideshow-next]");
        const currentEl = element.querySelector(".b-slideshow__current");
        const navigation = element.querySelector(".b-slideshow__navigation");

        if (!showArrows && navigation) {
            navigation.hidden = true;
        }

        if (showArrows && prevBtn) {
            prevBtn.addEventListener("click", () => {
                contentSplide.go("<");
            });
        }

        if (showArrows && nextBtn) {
            nextBtn.addEventListener("click", () => {
                contentSplide.go(">");
            });
        }

        // Update counter on slide change
        if (currentEl) {
            contentSplide.on("move", (newIndex) => {
                currentEl.textContent = newIndex + 1;
            });
        }

        // Update button states
        const updateButtonStates = () => {
            if (!rewind) {
                const index = contentSplide.index;
                const length = contentSplide.length;

                if (prevBtn) {
                    prevBtn.disabled = index === 0;
                }
                if (nextBtn) {
                    nextBtn.disabled = index === length - 1;
                }
            }
        };

        if (showArrows) {
            contentSplide.on("move", updateButtonStates);
            updateButtonStates();
        }

        const updateTextAccents = () => {
            requestAnimationFrame(() => {
                updateTextAccentMeasurements(element);
            });
        };

        updateTextAccents();

        // Recalculate line-based accent alignment on slide updates and resize.
        contentSplide.on("moved", updateTextAccents);
        window.addEventListener("resize", debounce(updateTextAccents, 120));
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(updateTextAccents);
        }
    });

    log(`Initialized ${slideshows.length} slideshow(s)`);
}

export default initSlideshow;

/**
 * Testimonials Slideshow
 * Single Splide instance showing testimonial cards
 */
export function initTestimonialsSlideshow() {
    const elements = document.querySelectorAll("[data-testimonials-slideshow]");

    if (elements.length === 0) {
        return;
    }

    elements.forEach((element) => {
        const slider = element.querySelector("[data-testimonials-slider]");

        if (!slider) {
            log("Testimonials slideshow: Missing slider element");
            return;
        }

        const speed = getSplideSpeed(
            element.dataset.splideSpeed,
            TRANSITION_SLIDESHOW_MULTIPLE.speed,
        );
        const showArrows = element.dataset.splideArrows !== "0";

        const splide = new Splide(slider, {
            type: "loop",
            speed: speed,
            easing: TRANSITION_SLIDESHOW_MULTIPLE.easing,
            autoplay: false,
            arrows: false,
            pagination: false,
            perPage: 3, // xl+
            perMove: 1,
            gap: "1.5rem",
            breakpoints: {
                1399: { perPage: 2 }, // md + lg
                991: { perPage: 1 }, // sm and below
            },
        });

        splide.mount();

        // Custom navigation
        const prevBtn = element.querySelector("[data-testimonials-prev]");
        const nextBtn = element.querySelector("[data-testimonials-next]");
        const currentEl = element.querySelector(".b-testimonials__current");
        const navigation = element.querySelector(".b-testimonials__navigation");

        if (!showArrows && navigation) {
            navigation.hidden = true;
        }

        if (showArrows && prevBtn) {
            prevBtn.addEventListener("click", () => splide.go("<"));
        }

        if (showArrows && nextBtn) {
            nextBtn.addEventListener("click", () => splide.go(">"));
        }

        if (currentEl) {
            splide.on("move", (newIndex) => {
                currentEl.textContent = newIndex + 1;
            });
        }

        // Loop type wraps endlessly — buttons never need disabling
    });

    log(`Initialized ${elements.length} testimonials slideshow(s)`);
}

/**
 * Image Gallery Slideshow
 * Single Splide instance showing one image at a time
 * Supports slide, fade, and loop transition types
 */
export function initImageGallerySlideshow() {
    const elements = document.querySelectorAll(
        "[data-image-gallery-slideshow]",
    );

    if (elements.length === 0) {
        return;
    }

    elements.forEach((element) => {
        const slider = element.querySelector("[data-image-gallery-slider]");

        if (!slider) {
            log("Image gallery slideshow: Missing slider element");
            return;
        }

        const type = element.dataset.splideType || "slide";
        const isLoop = type === "loop";
        const speed = getSplideSpeed(
            element.dataset.splideSpeed,
            TRANSITION_SLIDESHOW_SINGLE.speed,
        );
        const autoplay = element.dataset.splideAutoplay === "1";
        const interval = parseInt(element.dataset.splideInterval, 10) || 5000;
        const showArrows = element.dataset.splideArrows !== "0";

        const splide = new Splide(slider, {
            type: isLoop ? "loop" : type,
            rewind: !isLoop,
            speed: speed,
            easing: TRANSITION_SLIDESHOW_SINGLE.easing,
            autoplay: autoplay,
            interval: interval,
            pauseOnHover: true,
            arrows: false,
            pagination: false,
            perPage: 1,
            perMove: 1,
        });

        splide.mount();

        const prevBtn = element.querySelector("[data-image-gallery-prev]");
        const nextBtn = element.querySelector("[data-image-gallery-next]");
        const currentEl = element.querySelector(
            ".b-image-gallery-slideshow__current",
        );
        const navigation = element.querySelector(
            ".b-image-gallery-slideshow__navigation",
        );

        if (!showArrows && navigation) {
            navigation.hidden = true;
        }

        if (showArrows && prevBtn) {
            prevBtn.addEventListener("click", () => splide.go("<"));
        }

        if (showArrows && nextBtn) {
            nextBtn.addEventListener("click", () => splide.go(">"));
        }

        if (currentEl) {
            splide.on("move", (newIndex) => {
                currentEl.textContent = newIndex + 1;
            });
        }

        if (!isLoop && showArrows) {
            const updateButtonStates = () => {
                const index = splide.index;
                const length = splide.length;
                if (prevBtn) prevBtn.disabled = index === 0;
                if (nextBtn) nextBtn.disabled = index === length - 1;
            };
            splide.on("move", updateButtonStates);
            updateButtonStates();
        }
    });

    log(`Initialized ${elements.length} image gallery slideshow(s)`);
}
